import { __assign } from "tslib";
import { useCallback, useState } from 'react';
import { useHighlightedOption } from '../../internal/components/options-list/utils/use-highlight-option';
import { useOpenState } from '../../internal/components/options-list/utils/use-open-state';
import { fireKeyboardEvent, fireNonCancelableEvent } from '../../internal/events';
import { KeyCode } from '../../internal/keycode';
import { getHighlightableItems, isItemGroup, isLinkItem } from './utils';
export var useButtonDropdown = function (_a) {
    var items = _a.items, onItemClick = _a.onItemClick, hasExpandableGroups = _a.hasExpandableGroups, _b = _a.isInRestrictedView, isInRestrictedView = _b === void 0 ? false : _b, _c = _a.isChild, isChild = _c === void 0 ? false : _c, usingMouse = _a.usingMouse;
    var _d = useState(), expandedItem = _d[0], setExpandedItem = _d[1];
    // we have two open dropdowns at the same time: parent and child
    // keyboard navigation is allowed only in one dropdown at a time
    var _e = useState(!isChild), allowKeyboardNavigation = _e[0], setAllowKeyboardNavigation = _e[1];
    var _f = useHighlightedOption({
        options: getHighlightableItems(items, hasExpandableGroups, isInRestrictedView, expandedItem),
        isInteractive: function (item) {
            return !!item && !item.disabled;
        }
    }), highlightedItem = _f.highlightedOption, moveHighlight = _f.moveHighlight, resetHighlight = _f.resetHighlight, goHome = _f.goHome, setHighlightedIndex = _f.setHighlightedIndex, highlightedIndex = _f.highlightedIndex;
    var _g = useOpenState({
        resetHighlight: resetHighlight
    }), isOpen = _g.isOpen, closeDropdown = _g.closeDropdown, toggleDropdown = _g.toggleDropdown;
    var closeExpanded = function () {
        setExpandedItem(undefined);
    };
    var activateGroup = function (group) {
        // if there is already open group and we're in restricted mode
        // when expandable categories are inside of parent dropdown
        // previously opened category will collapse
        // and number of highlightable elements will change
        // that is why we need to set correct highlightedIndex
        if (expandedItem && isInRestrictedView) {
            var expandedIndex = items.indexOf(expandedItem);
            if (highlightedIndex > expandedIndex) {
                var newHighlightedIndex = highlightedIndex - expandedItem.items.length;
                setHighlightedIndex(newHighlightedIndex);
            }
        }
        setExpandedItem(expandedItem !== group ? group : undefined);
    };
    var onItemActivate = function (item, event) {
        if (isItemGroup(item)) {
            activateGroup(item);
        }
        else {
            if (onItemClick) {
                // information about the event is needed only for breadcrumbs and link items
                // we do not make it public for ButtonDropdown items
                fireNonCancelableEvent(onItemClick, __assign({ id: item.id || 'undefined' }, (isLinkItem(item) && { event: event })));
            }
            closeDropdown();
        }
    };
    var verticalNavigation = function (direction, isSecondaryFocus, e) {
        // do not allow vertical navigation when dropdown is closed
        if (!isOpen && !highlightedItem) {
            return;
        }
        // combining keyboard and mouse.
        // When we have a child dropdown opened with a mouse
        // But continue navigating with the keyboard through parent items,
        // opened child dropdown should collapse
        if (!isChild && expandedItem && allowKeyboardNavigation && !isInRestrictedView) {
            setExpandedItem(undefined);
        }
        if (isChild && !allowKeyboardNavigation) {
            toggleDropdown();
            return;
        }
        // move highlight only for primary focus - child dropdown
        if (!isSecondaryFocus) {
            moveHighlight(direction);
        }
        e.preventDefault();
    };
    var openAndSelectFirst = function (e) {
        toggleDropdown();
        moveHighlight(1);
        e.preventDefault();
    };
    var actOnMobileExpandableDropdown = function (e, isSecondaryFocus) {
        if (!highlightedItem) {
            openAndSelectFirst(e);
            return;
        }
        // when event is fired for child dropdown item
        // close parent dropdown
        if (isSecondaryFocus) {
            closeDropdown();
            closeExpanded();
            return;
        }
        if (highlightedItem && !isLinkItem(highlightedItem)) {
            onItemActivate(highlightedItem, e);
        }
    };
    var toggleExpandableCategory = function (e) {
        if (!highlightedItem) {
            setAllowKeyboardNavigation(!allowKeyboardNavigation);
            openAndSelectFirst(e);
        }
        else {
            onItemActivate(highlightedItem, e);
        }
    };
    var actOnParentDropdown = function (e, isSecondaryFocus) {
        // if there is no highlighted item we act on the trigger by opening or closing dropdown
        if (!highlightedItem) {
            if (isOpen) {
                toggleDropdown();
            }
            else {
                openAndSelectFirst(e);
            }
            return;
        }
        // if category is open
        if (isSecondaryFocus) {
            setAllowKeyboardNavigation(!allowKeyboardNavigation);
            resetHighlight();
            closeDropdown();
            closeExpanded();
            return;
        }
        // if category is closed
        if (isItemGroup(highlightedItem)) {
            setAllowKeyboardNavigation(!allowKeyboardNavigation);
        }
        if (highlightedItem && !isLinkItem(highlightedItem)) {
            onItemActivate(highlightedItem, e);
        }
    };
    var activate = function (e, isSecondaryFocus) {
        usingMouse.current = false;
        // if item is a link we rely on default behavior of an anchor, no need to prevent
        if (!highlightedItem || !isLinkItem(highlightedItem)) {
            e.preventDefault();
        }
        if (isInRestrictedView) {
            actOnMobileExpandableDropdown(e, isSecondaryFocus);
            return;
        }
        if (isChild) {
            toggleExpandableCategory(e);
            return;
        }
        actOnParentDropdown(e, isSecondaryFocus);
    };
    var useKeyboardDownHandler = function (moveHighlight) {
        return useCallback(function (e) {
            // in case when we have to keep two focuses
            // one - on selected category element
            // second - on selected child in category
            // we should avoid manipulations to the category header
            // when user navigates through the children
            // this does not apply when we're in restricted mode
            var isCategoryHeader = !!expandedItem && expandedItem === highlightedItem;
            var isSecondaryFocus = !isInRestrictedView && isCategoryHeader;
            switch (e.detail.keyCode) {
                case KeyCode.down: {
                    verticalNavigation(1, isSecondaryFocus, e);
                    break;
                }
                case KeyCode.up: {
                    verticalNavigation(-1, isSecondaryFocus, e);
                    break;
                }
                case KeyCode.space: {
                    // Prevent scrolling the list of items and highlighting the trigger
                    usingMouse.current = false;
                    e.preventDefault();
                    break;
                }
                case KeyCode.enter: {
                    activate(e, isSecondaryFocus);
                    break;
                }
                case KeyCode.left:
                case KeyCode.right: {
                    if (highlightedItem && isItemGroup(highlightedItem)) {
                        // hand keyboard navigation to category dropdown and open it
                        setAllowKeyboardNavigation(!allowKeyboardNavigation);
                        onItemActivate(highlightedItem, e);
                    }
                    else if (isChild) {
                        //activating first item in child dropdown
                        moveHighlight(1);
                        // take charge of keyboard navigation
                        setAllowKeyboardNavigation(!allowKeyboardNavigation);
                    }
                    e.preventDefault();
                    break;
                }
                case KeyCode.escape: {
                    toggleDropdown();
                    closeExpanded();
                    e.preventDefault();
                    break;
                }
                case KeyCode.tab: {
                    closeDropdown();
                    closeExpanded();
                    break;
                }
            }
        }, [moveHighlight, highlightedItem, usingMouse]);
    };
    var useKeyboardUpHandler = function (moveHighlight) {
        return useCallback(function (e) {
            var isCategoryHeader = !!expandedItem && expandedItem === highlightedItem;
            var isSecondaryFocus = !isInRestrictedView && isCategoryHeader;
            switch (e.detail.keyCode) {
                // We need to handle activating items with Space separately because there is a bug
                // in Firefox where changing the focus during a Space keydown event it will trigger
                // unexpected click events on the new element: https://bugzilla.mozilla.org/show_bug.cgi?id=1220143
                case KeyCode.space: {
                    activate(e, isSecondaryFocus);
                    break;
                }
            }
        }, [moveHighlight, highlightedItem, usingMouse]);
    };
    var handleKeyDown = useKeyboardDownHandler(moveHighlight);
    var onKeyDown = function (event) { return fireKeyboardEvent(handleKeyDown, event); };
    var handleKeyUp = useKeyboardUpHandler(moveHighlight);
    var onKeyUp = function (event) { return fireKeyboardEvent(handleKeyUp, event); };
    var onTriggerClick = function () {
        setAllowKeyboardNavigation(true);
    };
    return {
        isOpen: isOpen,
        highlightedItem: highlightedItem,
        handleKeyDown: handleKeyDown,
        onKeyDown: onKeyDown,
        onKeyUp: onKeyUp,
        onItemActivate: onItemActivate,
        expandedItem: expandedItem,
        toggleDropdown: toggleDropdown,
        closeExpanded: closeExpanded,
        resetHighlight: resetHighlight,
        goHome: goHome,
        onTriggerClick: onTriggerClick
    };
};
//# sourceMappingURL=use-button-dropdown.js.map